import { Buffer } from 'buffer';

import { type FunctionComponent, useCallback, useState } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Button, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import DownloadFile from '../../../images/download-file-icon.svg';
import RosterModal from '../components/RosterModal';
import InviteStudentsContainer from '../InviteStudentsContainer';
import { type InviteStudentsAlert } from '../types/inviteStudentsTypes';
import { validateStudentInvitations, findDuplicateEmailIndexes, findDuplicateExtUserIdIndexes } from '../utils/inviteStudentsValidation';
import parseStudentsCsv from '../utils/parseStudentsCsv';
import { removeBlankStudents } from '../utils/removeBlankStudents';

interface DropZoneProps {
    isDragActive: boolean;
}

const InfoText = styled.div`
    margin-bottom: 1rem;
`;

const StyledDropZone = styled.div<DropZoneProps>`
    min-height: 200px;

    background: ${(props) =>
        props.isDragActive
            ? props.theme.colors.secondary.hoverBackground
            : props.theme.colors.secondary.lightBackground};

    border: 2px dashed #b0b0b0;
    box-sizing: border-box;
    border-radius: 10px;
    margin-bottom: 8px;
`;

const StyledCloudIconContainer = styled.div`
    text-align: center;
    padding-top: 30px;
`;

const StyledChooseFileContainer = styled.div`
    margin-top: 20px;
    margin-bottom: 25px;
    text-align: center;
`;

const StyledDownloadFileIcon = styled.img`
    margin-right: 8px;
`;

const StyledDownloadSample = styled.div`
    margin-top: 26px;
    margin-bottom: 45px;
    display: flex;
    max-width: 210px;
`;

const StyledCloudUploadIcon = withStyles(() => ({
    root: {
        height: '55px',
        width: '70px'
    }
}))(CloudUploadIcon);

type FileError = 'file-too-large' | 'too-many-files' | 'file-invalid-type' | string;

const UploadInvite: FunctionComponent = () => {
    const { t } = useTranslation('invite_students');
    const { alerts, setAlerts, setErrorIndexes, setDuplicateEmailIndexes, setDuplicateExtUserIdIndexes, setStudents, validationErrorAlert } =
        useContainer(InviteStudentsContainer);
    const [showModal, setShowModal] = useState(false);

    const openRosterModal = () => setShowModal(true);
    const closeRosterModal = () => setShowModal(false);

    const onDropAccepted = useCallback(
        (acceptedFiles: File[]) => {
            const isCsv = acceptedFiles?.[0]?.name?.endsWith('.csv') ?? false;

            if (!isCsv) {
                const errorAlert: InviteStudentsAlert = {
                    category: 'uploadError',
                    message: t('upload-invite.file-upload-error.file-invalid-type'),
                    severity: 'error'
                };
                setAlerts((currentAlerts) => [...currentAlerts, errorAlert]);
            } else {
                const reader = new FileReader();

                reader.onload = async (e: ProgressEvent<FileReader>) => {
                    const fileContents = (e?.target?.result as ArrayBuffer) ?? [];
                    const contentsAsUint8Array = new Uint8Array(fileContents);
                    const fileText = Buffer.from(contentsAsUint8Array).toString('utf8');

                    const newAlerts: InviteStudentsAlert[] = [];
                    const formattedData = parseStudentsCsv(fileText);
                    setStudents((currentStudents) => {
                        const blanksRemoved = removeBlankStudents(currentStudents);
                        return [...blanksRemoved, ...formattedData];
                    });

                    const sucessAlert: InviteStudentsAlert = {
                        category: 'uploadSuccess',
                        message: t('alerts.upload-success', { numberOfStudents: formattedData.length }),
                        severity: 'success',
                        onClose: () => {}
                    };
                    newAlerts.push(sucessAlert);

                    const newErrorIndexes = validateStudentInvitations(formattedData);
                    setErrorIndexes(newErrorIndexes);
                    setDuplicateEmailIndexes(findDuplicateEmailIndexes(formattedData));
                    setDuplicateExtUserIdIndexes(findDuplicateExtUserIdIndexes(formattedData));

                    if (newErrorIndexes.length > 0) {
                        newAlerts.push(validationErrorAlert);
                    }

                    setAlerts((currentAlerts) => [...currentAlerts, ...newAlerts]);
                };

                acceptedFiles.forEach((file) => {
                    reader.readAsArrayBuffer(file);
                });
            }
        },
        [setStudents, t, setErrorIndexes, setDuplicateEmailIndexes, setDuplicateExtUserIdIndexes, setAlerts, validationErrorAlert]
    );

    const onDropRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            const buildErrorMessage = (fileError: FileError) => {
                switch (fileError) {
                    case 'too-many-files':
                    case 'file-too-large':
                    case 'file-invalid-type':
                        return t(`upload-invite.file-upload-error.${fileError}`);
                    default:
                        return t('upload-invite.file-upload-error.file-invalid-type');
                }
            };

            const errorAlert: InviteStudentsAlert = {
                category: 'uploadError',
                message: buildErrorMessage(fileRejections[0].errors[0].code),
                severity: 'error'
            };
            setAlerts([...alerts, errorAlert]);
        },
        [alerts, setAlerts, t]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted,
        onDropRejected,
        accept: '', // MIME types not properly set on windows. Validate fileType in onDropAccepted
        maxSize: 2097152, // 2mb
        maxFiles: 1
    });

    return (
        <>
            <InfoText>{t('upload-invite.upload-csv')}</InfoText>
            <StyledDropZone {...getRootProps()} isDragActive={isDragActive}>
                <input {...getInputProps()} />
                <StyledCloudIconContainer>
                    <StyledCloudUploadIcon color="primary" />
                </StyledCloudIconContainer>
                <Typography align="center" color="textSecondary">
                    {t('upload-invite.drag-and-drop')}
                </Typography>
                <StyledChooseFileContainer>
                    <Button color="primary" variant="contained">
                        {t('upload-invite.choose-file')}
                    </Button>
                </StyledChooseFileContainer>
            </StyledDropZone>
            <StyledDownloadSample>
                <StyledDownloadFileIcon src={DownloadFile} alt={t('upload-invite.download-file-icon')} />
                <Button onClick={openRosterModal}>{t('upload-invite.view-sample')}</Button>
            </StyledDownloadSample>

            <RosterModal open={showModal} onClose={closeRosterModal} />
        </>
    );
};

export default UploadInvite;
